// ─── Render backend client ──────────────────────────────────────────────────
// Thin abstraction over the "start a render job → poll progress → get
// output URL" flow. The mock implementation is kept for local dev when
// VITE_VIDEO_EXPORT_MOCK is not "false"; the real implementation calls the
// `render-video-start` and `render-video-status` Supabase Edge Functions
// which fan out the work to AWS Lambda via Remotion.

import { supabase } from "../supabase";
import type { RenderInputProps } from "./types";

export interface RenderProgress {
  /** 0..1 completion ratio. */
  progress: number;
  /**
   * Label técnico de la etapa ("Renderizando en AWS…", "Codificando video…").
   * Sirve para logs/diagnóstico — la UI NO lo muestra: el texto que ve el
   * usuario sale de `renderMessages.ts`, derivado del progreso.
   */
  label: string;
}

export interface RenderResult {
  /** HTTPS URL the user can download / play. */
  outputUrl: string;
  /** Size in bytes, if known. */
  sizeBytes?: number;
}

export interface RenderHandle {
  /** Subscribe to progress updates. Returns an unsubscribe fn. */
  onProgress: (cb: (p: RenderProgress) => void) => () => void;
  /** Resolves when the render finishes, rejects on error. */
  result: Promise<RenderResult>;
  /**
   * Corta el seguimiento del render. Idempotente.
   *
   * Por defecto SÓLO deja de mirar (para el polling local): el render sigue
   * su curso en el servidor y el video queda guardado igual. Es lo que
   * corresponde cuando se cierra el panel de exportar, porque cerrar una
   * ventana no es pedir que se cancele el trabajo.
   *
   * Con `{ abortarEnServidor: true }` sí se aborta de verdad en Lambda y se
   * libera la cuota. Eso es lo que hace el botón "Cancelar", que es el único
   * lugar donde la persona pidió explícitamente cancelar.
   */
  cancel: (opciones?: { abortarEnServidor?: boolean }) => void;
}

const MOCK = import.meta.env.VITE_VIDEO_EXPORT_MOCK !== "false";

// ── Mock implementation ────────────────────────────────────────────────────
// Used until the Edge Functions are deployed. Simulates a ~6s render with
// smooth progress updates and resolves with a fixed sample URL so the UI
// can be fully exercised (including the download button).
function startMockRender(input: RenderInputProps): RenderHandle {
  const listeners = new Set<(p: RenderProgress) => void>();
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const emit = (p: RenderProgress) => {
    listeners.forEach((fn) => {
      try {
        fn(p);
      } catch {}
    });
  };

  // Fake timeline: quick startup, slow middle (encoding), fast finish.
  const STAGES: Array<{ at: number; progress: number; label: string }> = [
    { at: 0, progress: 0.02, label: "Preparando proyecto…" },
    { at: 400, progress: 0.1, label: "Resolviendo medios…" },
    { at: 900, progress: 0.25, label: "Inicializando renderer…" },
    { at: 1600, progress: 0.45, label: "Renderizando frames…" },
    { at: 3000, progress: 0.7, label: "Renderizando frames…" },
    { at: 4200, progress: 0.88, label: "Codificando video…" },
    { at: 5200, progress: 0.97, label: "Subiendo resultado…" },
  ];

  const result = new Promise<RenderResult>((resolve, reject) => {
    STAGES.forEach((s) => {
      timer = setTimeout(() => {
        if (cancelled) return;
        emit({ progress: s.progress, label: s.label });
      }, s.at);
    });
    setTimeout(() => {
      if (cancelled) {
        reject(new Error("Render cancelado"));
        return;
      }
      emit({ progress: 1, label: "¡Listo!" });
      // Sample video URL that any browser can play. In real life this will
      // be the signed S3 URL returned by the Lambda render.
      resolve({
        outputUrl: "https://vjs.zencdn.net/v/oceans.mp4",
        sizeBytes: undefined,
      });
    }, 6000);
  });

  // Suppress unused-var lint; `input` will be used by the real client.
  void input;

  return {
    onProgress(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    result,
    cancel() {
      // El mock no tiene servidor que avisar: la opción se ignora a propósito.
      cancelled = true;
      if (timer) clearTimeout(timer);
    },
  };
}

// ── Real implementation ────────────────────────────────────────────────────
// Calls the deployed Supabase Edge Functions:
//   • POST /functions/v1/render-video-start  → starts a Lambda render and
//     returns `{ jobId }` for tracking.
//   • POST /functions/v1/render-video-status → polled every POLL_MS until
//     the job reports `done` or `error`.
//
// The Edge Functions handle auth, project ownership checks, and inserting
// rows into `render_jobs`. The frontend just needs the user's session so
// supabase-js attaches the JWT automatically.

const POLL_MS = 2500;

interface StartResponse {
  jobId: string;
  error?: string;
}

interface StatusResponse {
  status: "pending" | "rendering" | "done" | "error" | "cancelled";
  progress: number;
  label: string;
  outputUrl?: string;
  sizeBytes?: number;
  durationSeconds?: number;
  errorMessage?: string;
  /**
   * Sólo viene en la respuesta a un `action: 'cancel'`. `false` significa que
   * la fila quedó cancelada pero AWS NO confirmó que soltó el render: puede
   * seguir ocupando cuota unos minutos. Con 10 slots de concurrencia y 9 por
   * render, exportar de nuevo enseguida choca contra "Rate Exceeded", así que
   * conviene avisarlo en vez de dar la cuota por libre.
   */
  liberadoEnAws?: boolean;
}

// We need the projectId on the start call. Today the serializer doesn't
// know about it (it's UI-state in VideoEditor.tsx), so we accept it on
// `startRender` as an extra param for the real path. The mock ignores it.
// Frontend call sites pass the current projectId in.
/**
 * Cuánto se sigue un render antes de darlo por cortado. Los reales tardan como mucho
 * ~3 min; uno que AWS ya no conoce quedaba "renderizando" para siempre
 * (ADR-2026-11-03-el-export-se-retoma-y-no-se-pisa).
 */
export const VIDA_MAXIMA_DEL_RENDER_MS = 20 * 60 * 1000;
export const MENSAJE_RENDER_CORTADO =
  "El export se cortó y no terminó. Volvé a exportar.";

function startRealRender(
  input: RenderInputProps,
  options: { projectId: string },
): RenderHandle {
  return seguirUnRender(async () => {
    const { data, error } = await supabase.functions.invoke<StartResponse>(
      "render-video-start",
      { body: { projectId: options.projectId, inputProps: input } },
    );
    if (error) throw new Error(error.message || "render-video-start failed");
    if (!data?.jobId) throw new Error(data?.error || "No jobId in response");
    return data.jobId;
  }, Date.now());
}

/**
 * Engancharse a un render que ya arrancó (el editor se recargó, se cerró el modal, o se
 * tocó Exportar con uno en curso): el mismo seguimiento que al arrancarlo
 * (ADR-2026-11-03-el-export-se-retoma-y-no-se-pisa).
 */
export function seguirRender(jobId: string, arrancoEn: number): RenderHandle {
  return seguirUnRender(async () => jobId, arrancoEn);
}

/**
 * El export de este proyecto que está corriendo, si hay uno. `render_jobs` se lee con
 * la sesión (RLS por empresa). Sólo cuenta lo que arrancó hace menos de
 * `VIDA_MAXIMA_DEL_RENDER_MS`: los colgados viejos no bloquean nada.
 */
export async function buscarExportEnCurso(
  projectId: string,
): Promise<{ jobId: string; arrancoEn: number } | null> {
  if (MOCK) return null;
  try {
    const desde = new Date(Date.now() - VIDA_MAXIMA_DEL_RENDER_MS).toISOString();
    const { data } = await supabase
      .from("render_jobs")
      .select("id, created_at")
      .eq("project_id", projectId)
      .in("status", ["pending", "rendering"])
      .gte("created_at", desde)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!data?.id) return null;
    return { jobId: data.id as string, arrancoEn: Date.parse(data.created_at as string) || Date.now() };
  } catch {
    return null;
  }
}

function seguirUnRender(
  arrancar: () => Promise<string>,
  arrancoEn: number,
): RenderHandle {
  const listeners = new Set<(p: RenderProgress) => void>();
  let cancelled = false;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  // Afuera del closure de `run` a propósito: `cancel()` lo necesita para
  // avisarle al servidor qué render abortar. Queda null si el usuario cancela
  // antes de que `render-video-start` haya respondido.
  let jobIdEnVuelo: string | null = null;

  const emit = (p: RenderProgress) => {
    listeners.forEach((fn) => {
      try {
        fn(p);
      } catch {}
    });
  };

  const result = new Promise<RenderResult>((resolve, reject) => {
    const run = async () => {
      // 1. Kick off the render (o tomar el que ya corre).
      let jobId: string;
      try {
        jobId = await arrancar();
        jobIdEnVuelo = jobId;
      } catch (err: any) {
        reject(new Error(err?.message || "No se pudo iniciar el render"));
        return;
      }

      if (cancelled) {
        reject(new Error("Render cancelado"));
        return;
      }

      emit({ progress: 0.02, label: "Renderizando en AWS…" });

      // 2. Poll status until done / error / cancelled.
      const poll = async () => {
        if (cancelled) {
          reject(new Error("Render cancelado"));
          return;
        }
        if (Date.now() - arrancoEn > VIDA_MAXIMA_DEL_RENDER_MS) {
          reject(new Error(MENSAJE_RENDER_CORTADO));
          return;
        }
        try {
          const { data, error } =
            await supabase.functions.invoke<StatusResponse>(
              "render-video-status",
              { body: { jobId } },
            );
          if (error)
            throw new Error(error.message || "render-video-status failed");
          if (!data) throw new Error("Empty response from status function");

          if (cancelled) {
            reject(new Error("Render cancelado"));
            return;
          }

          emit({ progress: data.progress, label: data.label });

          if (data.status === "done") {
            if (!data.outputUrl) {
              reject(new Error("Render terminó pero no devolvió URL"));
              return;
            }
            resolve({ outputUrl: data.outputUrl, sizeBytes: data.sizeBytes });
            return;
          }
          if (data.status === "error") {
            reject(new Error(data.errorMessage || "El render falló"));
            return;
          }
          if (data.status === "cancelled") {
            reject(new Error("Render cancelado"));
            return;
          }

          // Still rendering — schedule next poll.
          pollTimer = setTimeout(poll, POLL_MS);
        } catch (err: any) {
          // Transient network error — retry once after a short delay before
          // giving up.
          if (!cancelled) {
            pollTimer = setTimeout(poll, POLL_MS * 2);
          } else {
            reject(err);
          }
        }
      };
      pollTimer = setTimeout(poll, POLL_MS);
    };

    run();
  });

  return {
    onProgress(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    result,
    cancel(opciones) {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      // Abortar en Lambda SÓLO si la persona lo pidió. Cerrar el panel de
      // exportar también llama a `cancel()` (para no recibir callbacks de un
      // modal que ya no está), y ahí abortar sería destruirle un render de
      // varios minutos por haber cerrado una ventana.
      //
      // Cuando sí corresponde, es fire-and-forget: la UI ya no espera nada. Si
      // la llamada falla (sin internet, servidor caído), la persona igual ve
      // su export cancelado; lo único que se pierde es liberar el slot
      // antes de tiempo, que se resuelve solo cuando el render termina. El
      // `.catch` vacío existe para no dejar una promesa rechazada sin manejar.
      if (opciones?.abortarEnServidor && jobIdEnVuelo) {
        void supabase.functions
          .invoke("render-video-status", {
            body: { jobId: jobIdEnVuelo, action: "cancel" },
          })
          .catch(() => {});
      }
    },
  };
}

export function startRender(
  input: RenderInputProps,
  options?: { projectId?: string },
): RenderHandle {
  if (MOCK) return startMockRender(input);
  if (!options?.projectId) {
    throw new Error(
      "startRender requiere projectId cuando VITE_VIDEO_EXPORT_MOCK=false",
    );
  }
  return startRealRender(input, { projectId: options.projectId });
}
